import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSurveyUrl } from "../hooks/useSurveyUrl";
import { trackSurveyStart } from "../lib/track";
import { fontSans, color as T, radius } from "../design/tokens";

// HAEVN Logo SVG — kept as a named export because Footer imports it.
export const HaevnLogo = ({ className = "", color = "#0B1220" }) => (
  <svg viewBox="0 0 1554.72 337.36" className={className} fill={color}>
    <g>
      <g>
        <path d="M48.51,97.71h109.87v44.85h19.21v-44.85h108.14c17.11,0,31.03,13.92,31.03,31.03s-13.92,31.03-31.03,31.03h-13.12v19.21h13.12c27.71,0,50.24-22.54,50.24-50.24s-22.54-50.24-50.24-50.24H48.51c-16.15,0-29.3-13.14-29.3-29.29v-.69c0-16.15,13.14-29.3,29.3-29.3s29.29,13.14,29.29,29.3v16.24h19.21v-16.24C97.01,21.76,75.25,0,48.51,0S0,21.76,0,48.51v.69c0,26.75,21.76,48.51,48.51,48.51Z" />
        <path d="M287.46,239.66h-109.88v-46.23h-19.21v46.23H50.24c-17.11,0-31.03-13.92-31.03-31.03s13.92-31.03,31.03-31.03h13.12v-19.21h-13.12c-27.71,0-50.24,22.54-50.24,50.24s22.54,50.24,50.24,50.24h237.22c16.15,0,29.3,13.14,29.3,29.3v.69c0,16.15-13.14,29.3-29.3,29.3s-29.29-13.14-29.29-29.3v-16.24h-19.21v16.24c0,26.75,21.76,48.51,48.5,48.51s48.51-21.76,48.51-48.51v-.69c0-26.75-21.76-48.51-48.51-48.51Z" />
        <path d="M177.59,49.2c0-16.54,13.45-29.99,29.99-29.99s29.99,13.45,29.99,29.99v15.55h19.21v-15.55c0-27.13-22.07-49.2-49.2-49.2s-49.2,22.07-49.2,49.2v15.55h19.21v-15.55Z" />
        <path d="M158.38,288.16c0,16.54-13.45,29.99-29.99,29.99s-29.99-13.45-29.99-29.99v-15.55h-19.21v15.55c0,27.13,22.07,49.2,49.2,49.2s49.2-22.07,49.2-49.2v-15.55h-19.21v15.55Z" />
        <polygon points="256.78 227.46 256.78 111.3 237.57 111.3 237.57 158.38 98.4 158.39 98.4 111.29 79.19 111.29 79.19 227.45 98.4 227.45 98.4 177.6 237.57 177.59 237.57 227.46 256.78 227.46" />
      </g>
      <g>
        <polygon points="597.4 151.71 477.49 151.71 477.49 53.9 445.95 53.9 445.95 283.47 477.49 283.47 477.49 182.45 597.4 182.45 597.4 283.47 629.1 283.47 629.1 53.9 597.4 53.9 597.4 151.71" />
        <polygon points="1090.09 84.63 1078.12 53.9 922.51 53.9 922.51 283.47 1090.09 283.47 1090.09 252.57 954.04 252.57 954.04 179.25 1050.1 179.25 1050.1 148.67 954.04 148.67 954.04 84.47 1090.09 84.63" />
        <polygon points="1221.82 239.28 1150.1 53.9 1117.44 53.9 1206.94 283.47 1236.55 283.47 1326.05 53.9 1293.23 53.9 1221.82 239.28" />
        <polygon points="1523.34 53.9 1523.34 226.95 1390.3 53.9 1362.77 53.9 1362.77 283.47 1394.15 283.47 1394.15 110.41 1527.18 283.47 1554.72 283.47 1554.72 53.9 1523.34 53.9" />
        <polygon points="761.87 53.9 665.18 283.47 699.12 283.47 775.96 98.72 852.33 283.47 886.43 283.47 789.73 53.9 761.87 53.9" />
      </g>
    </g>
  </svg>
);

// Global primary navigation. Order and labels match the approved Phase 1
// specification (HAEVN Website Repositioning Report). "Know Yourself" is
// intentionally retired from the primary nav; the /blog route stays but
// is presented as "Journal".
//
// NOTE on "Log In": HAEVN has no public member-login destination on the
// marketing site — members enter the product via survey.haevn.co (the
// "Join the Network" CTA). /admin is a staff-only destination and is
// intentionally NOT surfaced from the public nav. A public member-login
// link can be added later once a member portal URL is supplied.
const navLinks = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/membership", label: "Membership" },
  { to: "/blog", label: "Journal" },
  { to: "/about", label: "About" },
];

export default function Navigation({ variant = "light" }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const SURVEY_URL = useSurveyUrl();

  const isActive = (to) => location.pathname === to;

  // Dark variant: used on hero sections (e.g. Home) so the nav sits
  // seamlessly on the dark navy backdrop. Logo + links + hamburger
  // render in white; the CTA inverts to a white pill.
  const dark = variant === "dark";
  const navBg = dark ? "#050810" : T.bg;
  const navBorder = dark ? "transparent" : T.line;
  const logoColor = dark ? "#FFFFFF" : T.ink;
  const linkMuted = dark ? "rgba(255,255,255,0.78)" : T.inkMuted;
  const linkActive = dark ? "#FFFFFF" : T.ink;
  const togglerColor = dark ? "#FFFFFF" : T.ink;
  const ctaBg = dark ? "#FFFFFF" : T.ink;
  const ctaFg = dark ? "#0B1220" : "#FFFFFF";

  return (
    <header
      className="sticky top-0 z-50"
      data-testid="global-nav"
      style={{
        fontFamily: fontSans,
        background: navBg,
        borderBottom: `1px solid ${navBorder}`,
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="HAEVN home" className="flex-shrink-0">
          <HaevnLogo className="h-6 w-auto" color={logoColor} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-sm transition-colors duration-200"
              style={{
                fontWeight: 500,
                color: isActive(link.to) ? linkActive : linkMuted,
              }}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {link.label}
            </Link>
          ))}

          {/* Log In — external, points to the member application at
              haevn.app. /admin is intentionally NOT surfaced from the
              public nav; it remains staff-only, direct-URL access only. */}
          <a
            href="https://haevn.app"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm transition-colors duration-200"
            style={{ fontWeight: 500, color: linkMuted }}
            data-testid="nav-login"
          >
            Log In
          </a>

          <a
            href={SURVEY_URL}
            onClick={() => trackSurveyStart("nav_desktop")}
            style={{
              background: ctaBg,
              color: ctaFg,
              padding: "10px 18px",
              borderRadius: radius.sm,
              fontSize: 14,
              fontWeight: 500,
              transition: "background-color 200ms ease",
              display: "inline-flex",
              alignItems: "center",
            }}
            data-testid="nav-cta"
          >
            Join the Network
          </a>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden"
          style={{ color: togglerColor }}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav
          id="mobile-nav"
          className="md:hidden px-6 py-6 space-y-5"
          style={{
            background: navBg,
            borderTop: `1px solid ${navBorder}`,
          }}
          data-testid="mobile-nav"
        >
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className="block text-base"
              style={{ fontWeight: 500, color: isActive(link.to) ? linkActive : linkMuted }}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {link.label}
            </Link>
          ))}
          {/* Log In — external, member application. */}
          <a
            href="https://haevn.app"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="block text-base"
            style={{ fontWeight: 500, color: linkMuted }}
            data-testid="mobile-nav-login"
          >
            Log In
          </a>
          <a
            href={SURVEY_URL}
            onClick={() => {
              trackSurveyStart("nav_mobile");
              setMobileOpen(false);
            }}
            className="inline-flex items-center justify-center w-full py-3 mt-2"
            style={{
              background: ctaBg,
              color: ctaFg,
              borderRadius: radius.sm,
              fontSize: 15,
              fontWeight: 500,
            }}
            data-testid="mobile-nav-cta"
          >
            Join the Network
          </a>
        </nav>
      )}
    </header>
  );
}
